import React, { useMemo, useState, useRef, useCallback } from "react";
import {useBuildingKey} from "../../Helpers/utils";
import BuildingImagesSection from "./BuildingImagesSection";
import PhotoGallery from "../../Components/PhotoGallery";
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import {BaseComponent, BuildingModel, PhotoModel} from "../../types/protocols";
import {translate} from "../../types/EnumTranslations";
import {BaseType} from "../../types/Enums";
import {useBuilding} from "../../Helpers/BuildingContext";
import { RELEVANT_BASE_TYPES } from "../../Helpers/baseTypeUtils";
import { useNavigate } from "react-router-dom";
import { BaseComponentImpl } from "../../types/implementations";
import { useFirebaseImage } from "../../Helpers/ImageLoader";
import { Fancybox } from "@fancyapps/ui";

interface BuildingImagesListProps {
    buildingData?: BuildingModel;
}

// Component for image preview with proper Firebase loading
const ImagePreview: React.FC<{
    photo: PhotoModel;
    zoomLevel: number;
    panPosition: { x: number; y: number };
    onWheel: (e: React.WheelEvent) => void;
    onClick: (imageUrl: string) => void;
    onMouseDown: (e: React.MouseEvent) => void;
    alt: string;
    isDragging: boolean;
}> = ({ photo, zoomLevel, panPosition, onWheel, onClick, onMouseDown, alt, isDragging }) => {
    const { src, isLoading, hasError } = useFirebaseImage(photo.storagePath);
    
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#666'
            }}>
                Laddar bild...
            </div>
        );
    }
    
    if (hasError) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                color: '#999',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
            }}>
                Kunde inte ladda bild
            </div>
        );
    }
    
    return (
        <img
            src={src}
            alt={alt}
            style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                transition: isDragging ? 'none' : 'transform 0.1s ease',
                objectFit: 'contain',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none'
            }}
            onWheel={onWheel}
            onClick={() => onClick(src)}
            onMouseDown={onMouseDown}
            draggable={false}
        />
    );
};

const BuildingImagesList: React.FC<BuildingImagesListProps> = ({ buildingData: propBuilding }) => {
    const buildingKey = useBuildingKey();
    const navigate = useNavigate();
    const { currentBuilding, loadingBuilding, buildingError } = useBuilding();
    
    // State for selected image and zoom
    const [selectedImage, setSelectedImage] = useState<{
        photo: PhotoModel;
        componentName: string;
        source: string;
        year: string;
    } | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [hasDragged, setHasDragged] = useState(false);
    const previewRef = useRef<HTMLDivElement>(null);

    // Helper functions
    const filterComponentsByType = useCallback((components: { [key: string]: BaseComponent } = {}, type: BaseType) =>
        Object.fromEntries(
            Object.entries(components)
                .filter(([_, component]) => component.type === type)
        ), []);

    const filterComponentsByRoomAndType = useCallback((components: { [key: string]: BaseComponent } = {}, type: BaseType, roomKey: string) =>
        Object.fromEntries(
            Object.entries(components)
                .filter(([_, component]) => component.type === type)
                .filter(([_, component]) => component.parentRoomKeys?.value?.some(key => key === roomKey))
        ), []);

    // Collect all images with metadata for the component list
    const allImagesWithMetadata = useMemo(() => {
        if (!currentBuilding?.model?.structure) return [];
        
        const structure = currentBuilding.model.structure;
        const images: {
            photo: PhotoModel;
            componentName: string;
            source: string;
            year: string;
            component: BaseComponentImpl;
        }[] = [];

        // Collect photos from building components
        const componentTypes = RELEVANT_BASE_TYPES;
        for (const type of componentTypes) {
            const components = filterComponentsByType(structure.components, type);
            Object.values(components).forEach(component => {
                const componentImpl = new BaseComponentImpl(component);
                if (component.photos) {
                    Object.values(component.photos).forEach(photo => {
                        const year = new Date(photo.date).getFullYear().toString();
                        images.push({
                            photo,
                            componentName: componentImpl.displayName(currentBuilding),
                            source: translate(type),
                            year,
                            component: componentImpl
                        });
                    });
                }
            });
        }

        // Collect photos from rooms
        if (structure.rooms) {
            Object.values(structure.rooms).forEach(room => {
                if (room.photos) {
                    Object.values(room.photos).forEach(photo => {
                        const year = new Date(photo.date).getFullYear().toString();
                        images.push({
                            photo,
                            componentName: room.label || translate(room.type),
                            source: 'Rum',
                            year,
                            component: null as any
                        });
                    });
                }

                // Collect photos from room components
                const roomComponentTypes = [BaseType.Wall, BaseType.Ceiling, BaseType.Floor];
                roomComponentTypes.forEach(type => {
                    const roomComponents = filterComponentsByRoomAndType(structure.components, type, room.key);
                    Object.values(roomComponents).forEach(component => {
                        const componentImpl = new BaseComponentImpl(component);
                        if (component.photos) {
                            Object.values(component.photos).forEach(photo => {
                                const year = new Date(photo.date).getFullYear().toString();
                                images.push({
                                    photo,
                                    componentName: `${room.label || translate(room.type)} - ${translate(type)}`,
                                    source: 'Rum',
                                    year,
                                    component: componentImpl
                                });
                            });
                        }
                    });
                });
            });
        }

        // Sort by date (newest first)
        return images.sort((a, b) => {
            const dateA = new Date(a.photo.date);
            const dateB = new Date(b.photo.date);
            return dateB.getTime() - dateA.getTime();
        });
    }, [currentBuilding, filterComponentsByType, filterComponentsByRoomAndType]);

    // Handle image selection
    const handleImageSelect = useCallback((image: { photo: PhotoModel; componentName: string; source: string; year: string }) => {
        setSelectedImage(image);
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
        setHasDragged(false);
    }, []);


    // Handle mouse wheel zoom
    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoomLevel(prev => Math.max(0.1, Math.min(5, prev * delta)));
    }, []);

    // Handle mouse down to start panning
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
        setHasDragged(false);
    }, [panPosition]);

    // Handle mouse move to pan
    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging) return;
        
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        
        // If moved more than 5 pixels, consider it a drag
        const distance = Math.sqrt(Math.pow(newX - panPosition.x, 2) + Math.pow(newY - panPosition.y, 2));
        if (distance > 5) {
            setHasDragged(true);
        }
        
        setPanPosition({ x: newX, y: newY });
    }, [isDragging, dragStart, panPosition]);

    // Handle mouse up to stop panning
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle preview click to open in FancyBox (only if not dragging)
    const handlePreviewClick = useCallback((imageUrl: string) => {
        // Don't open FancyBox if the user was dragging
        if (hasDragged) {
            return;
        }
        
        if (selectedImage && imageUrl) {
            Fancybox.show([{
                src: imageUrl,
                caption: `${selectedImage.componentName} - ${selectedImage.year}`
            }]);
        }
    }, [selectedImage, hasDragged]);

    if (loadingBuilding) return <p></p>;
    if (buildingError) return <p style={{ color: "#ff0000" }}>{buildingError}</p>;
    if (!buildingKey) return <p>Byggnadsnyckel saknas</p>;

    if (loadingBuilding) return <p>Laddar byggnad</p>
    if (buildingError) return <p>Fel vid hämtning av byggnad</p>
    if (!currentBuilding) return <p>Byggnad saknas</p>

    const structure = currentBuilding.model.structure;
    if (!structure) {
        return <p>Structure missing</p>
    }

    // Helper function to check if a room has any images
    const roomHasImages = (room: any) => {
        // Check if room has overview photos
        if (room.photos && Object.values(room.photos).length > 0) {
            return true;
        }

        // Check if room has any component photos (walls, ceiling, floor)
        const roomComponents = [
            ...Object.values(filterComponentsByRoomAndType(structure.components, BaseType.Wall, room.key)),
            ...Object.values(filterComponentsByRoomAndType(structure.components, BaseType.Ceiling, room.key)),
            ...Object.values(filterComponentsByRoomAndType(structure.components, BaseType.Floor, room.key))
        ];

        return roomComponents.some(component =>
            component.photos && Object.values(component.photos).length > 0
        );
    };

    // Filter rooms to only include those with images
    const roomsWithImages = structure.rooms ?
        Object.values(structure.rooms).filter(roomHasImages) : [];

    // Check if there are any images at all in the building
    const hasAnyImages = () => {
        // Check building component sections
        const componentTypes = RELEVANT_BASE_TYPES;

        for (const type of componentTypes) {
            const components = filterComponentsByType(structure.components, type);
            const hasComponentImages = Object.values(components).some(component =>
                component.photos && Object.values(component.photos).length > 0
            );
            if (hasComponentImages) return true;
        }

        // Check if any rooms have images
        if (roomsWithImages.length > 0) return true;

        return false;
    };


    // If no images at all, show message
    if (!hasAnyImages()) {
        return (
            <div style={{
                backgroundColor: "#f4f4f4",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>
                <Helmet>
                    <title>Samtliga bilder: {currentBuilding?.summary.name}</title>
                </Helmet>

                <div style={{ flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
                        <BreadcrumbComponent
                            items={[
                                {text: currentBuilding?.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                                {text: "Samtliga bilder"}
                            ]}
                        />
                        <button
                            onClick={() => navigate(`/building/${buildingKey}/editImages`)}
                            style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Hantera bilder
                        </button>
                    </div>
                </div>

                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    fontSize: "18px",
                    color: "#666"
                }}>
                    Inga bilder att visa
                </div>
            </div>
        );
    }

    return (
        <div style={{
            backgroundColor: "#f4f4f4",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            <Helmet>
                <title>Samtliga bilder: {currentBuilding?.summary.name}</title>
            </Helmet>

            <div style={{ flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
                    <BreadcrumbComponent
                        items={[
                            {text: currentBuilding?.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                            {text: "Samtliga bilder"}
                        ]}
                    />
                    <button
                        onClick={() => navigate(`/building/${buildingKey}/editImages`)}
                        style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Hantera bilder
                    </button>
                </div>
            </div>

            <div style={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden'
            }}>
                {/* Left Panel - Original Structure (35% width, min 500px) */}
                <div style={{
                    width: '650px',
                    backgroundColor: '#f4f4f4',
                    borderRight: '1px solid #dee2e6',
                    overflow: 'auto',
                    padding: '20px'
                }}>
                    <div style={{display: 'flex', gap: '40px', flexDirection: 'column'}}>
                        <BuildingImagesSection
                            title={translate(BaseType.Roof)}
                            components={filterComponentsByType(structure.components, BaseType.Roof)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Facade)}
                            components={filterComponentsByType(structure.components, BaseType.Facade)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Foundation)}
                            components={filterComponentsByType(structure.components, BaseType.Foundation)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Chimney)}
                            components={filterComponentsByType(structure.components, BaseType.Chimney)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.RoofDrainage)}
                            components={filterComponentsByType(structure.components, BaseType.RoofDrainage)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Utility)}
                            components={filterComponentsByType(structure.components, BaseType.Utility)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        <BuildingImagesSection
                            title={translate(BaseType.Outdoor)}
                            components={filterComponentsByType(structure.components, BaseType.Outdoor)}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            onImageClick={handleImageSelect}
                            selectedImageKey={selectedImage?.photo.key}
                        />

                        {roomsWithImages.map((room, index) => (
                            <div key={index} >
                                <h3 key={room.key}>{room.label ?? translate(room.type)}</h3>

                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "40px",
                                        borderRadius: "15px",
                                        padding: "30px",
                                        backgroundColor: "#fff",
                                        textAlign: "left"
                                    }}
                                >
                                    {room.photos && Object.values(room.photos).length > 0 && (
                                        <div>
                                            <h4>Översiktsbilder</h4>
                                            <PhotoGallery
                                                photos={room.photos}
                                                buildingKey={buildingKey}
                                                onImageClick={(photo, url, index) => {
                                                    // If this image is already selected, don't call handleImageSelect (let PhotoGallery handle FancyBox)
                                                    if (selectedImage?.photo.key === photo.key) {
                                                        return;
                                                    }
                                                    const year = new Date(photo.date).getFullYear().toString();
                                                    handleImageSelect({
                                                        photo,
                                                        componentName: room.label || translate(room.type),
                                                        source: 'Rum',
                                                        year
                                                    });
                                                }}
                                            />
                                        </div>
                                    )}

                                    <BuildingImagesSection
                                        title="Innerväggar"
                                        components={filterComponentsByRoomAndType(structure.components, BaseType.Wall, room.key)}
                                        building={currentBuilding}
                                        buildingKey={buildingKey}
                                        useDivider={true}
                                        onImageClick={handleImageSelect}
                                        selectedImageKey={selectedImage?.photo.key}
                                    />

                                    <BuildingImagesSection
                                        title="Innertak"
                                        components={filterComponentsByRoomAndType(structure.components, BaseType.Ceiling, room.key)}
                                        building={currentBuilding}
                                        buildingKey={buildingKey}
                                        useDivider={true}
                                        onImageClick={handleImageSelect}
                                        selectedImageKey={selectedImage?.photo.key}
                                    />

                                    <BuildingImagesSection
                                        title="Innergolv"
                                        components={filterComponentsByRoomAndType(structure.components, BaseType.Floor, room.key)}
                                        building={currentBuilding}
                                        buildingKey={buildingKey}
                                        useDivider={true}
                                        onImageClick={handleImageSelect}
                                        selectedImageKey={selectedImage?.photo.key}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Panel - Image Preview (65% width) */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#fff',
                    overflow: 'hidden'
                }}>
                    {selectedImage ? (
                        <div
                            ref={previewRef}
                            style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                cursor: isDragging ? 'grabbing' : 'grab'
                            }}
                            onWheel={handleWheel}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <ImagePreview
                                photo={selectedImage.photo}
                                zoomLevel={zoomLevel}
                                panPosition={panPosition}
                                onWheel={handleWheel}
                                onClick={handlePreviewClick}
                                onMouseDown={handleMouseDown}
                                alt={`${selectedImage.componentName} - ${selectedImage.year}`}
                                isDragging={isDragging}
                            />
                        </div>
                    ) : (
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#666',
                            fontSize: '18px'
                        }}>
                            Välj en bild från listan för att visa förhandsvisning
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BuildingImagesList;